import React from 'react';
import { FiCheckCircle, FiCircle, FiDroplet } from 'react-icons/fi';
import './TodayNutrition.css';

const TodayNutrition = ({ logs = [], targets, onQuickAdd }) => {
  const calculateProgress = () => {
    if (!targets) {
      return { calories: 0, protein: 0, carbs: 0, fat: 0 };
    }

    const totals = logs.reduce(
      (acc, log) => ({
        calories: acc.calories + (log.totalCalories || 0),
        protein: acc.protein + (log.totalProtein || 0),
        carbs: acc.carbs + (log.totalCarbs || 0),
        fat: acc.fat + (log.totalFat || 0),
      }),
      { calories: 0, protein: 0, carbs: 0, fat: 0 }
    );

    return {
      calories: {
        current: Math.round(totals.calories),
        target: Math.round(targets.calories || 0),
        percentage: Math.min(100, (totals.calories / (targets.calories || 1)) * 100),
      },
      protein: {
        current: Math.round(totals.protein),
        target: Math.round(targets.protein?.grams || targets.protein || 0),
        percentage: Math.min(100, (totals.protein / (targets.protein?.grams || targets.protein || 1)) * 100),
      },
      carbs: {
        current: Math.round(totals.carbs),
        target: Math.round(targets.carbs?.grams || targets.carbs || 0),
        percentage: Math.min(100, (totals.carbs / (targets.carbs?.grams || targets.carbs || 1)) * 100),
      },
      fat: {
        current: Math.round(totals.fat),
        target: Math.round(targets.fat?.grams || targets.fat || 0),
        percentage: Math.min(100, (totals.fat / (targets.fat?.grams || targets.fat || 1)) * 100),
      },
    };
  };

  const getCurrentMealType = () => {
    const hour = new Date().getHours();
    if (hour < 11) return 'Breakfast';
    if (hour < 15) return 'Lunch';
    if (hour < 18) return 'Snack';
    return 'Dinner';
  };

  const getMealStatus = () => {
    const mealTypes = ['Breakfast', 'Lunch', 'Dinner'];
    return mealTypes.map(type => {
      const logged = logs.some(log => log.mealType === type);
      return { type, logged };
    });
  };

  const progress = calculateProgress();
  const mealStatus = getMealStatus();

  return (
    <div className="today-nutrition-widget">
      <div className="widget-header">
        <h3>Today's Nutrition</h3>
        <span className="date-badge">{new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      </div>

      {/* Macro Progress Rings */}
      <div className="macro-rings">
        {['calories', 'protein', 'carbs', 'fat'].map((macro) => (
          <div key={macro} className="macro-ring-item">
            <div className="ring-container">
              <svg viewBox="0 0 100 100" className="progress-ring">
                <circle
                  className="ring-background"
                  cx="50"
                  cy="50"
                  r="45"
                  strokeWidth="8"
                  fill="none"
                />
                <circle
                  className={`ring-progress ${macro}`}
                  cx="50"
                  cy="50"
                  r="45"
                  strokeWidth="8"
                  fill="none"
                  strokeDasharray={`${2 * Math.PI * 45}`}
                  strokeDashoffset={`${2 * Math.PI * 45 * (1 - progress[macro].percentage / 100)}`}
                  transform="rotate(-90 50 50)"
                />
              </svg>
              <div className="ring-text">
                <span className="ring-value">{progress[macro].current}</span>
                <span className="ring-target">/ {progress[macro].target}</span>
              </div>
            </div>
            <span className="macro-label">{macro.charAt(0).toUpperCase() + macro.slice(1)}</span>
          </div>
        ))}
      </div>

      {/* Meal Checklist */}
      <div className="meal-checklist">
        <h4>Meals Logged</h4>
        <div className="checklist-items">
          {mealStatus.map(({ type, logged }) => (
            <div key={type} className={`checklist-item ${logged ? 'logged' : ''}`}>
              {logged ? (
                <FiCheckCircle className="check-icon checked" />
              ) : (
                <FiCircle className="check-icon" />
              )}
              <span>{type}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Quick Add Button */}
      <button className="quick-add-btn" onClick={onQuickAdd}>
        <FiDroplet />
        Quick Add {getCurrentMealType()}
      </button>

      {/* Remaining Macros */}
      {targets && (
        <div className="remaining-macros">
          <span className="remaining-label">Remaining:</span>
          <div className="remaining-values">
            <span>{Math.max(0, progress.calories.target - progress.calories.current)} cal</span>
            <span>{Math.max(0, progress.protein.target - progress.protein.current)}g protein</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default TodayNutrition;
